import type { AnyAction } from '@reduxjs/toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Dispatch } from 'redux';
import createGenericSlice from '@/store/reducers/sliceTemplate';
import { handleResponseError } from '@/core/utils/handleResponseError';
import { apiUrl } from '@/api/axios';
import API_ENDPOINTS from '@/api/apiConfig';
import type {
	NormalizedWidgetListItem,
	WidgetsListState,
} from '@/store/types/widgetsListTypes';
import { parseWidgetsListResponse } from '@/store/utils/parseWidgetsListResponse';

const widgetsListSlice = createGenericSlice<NormalizedWidgetListItem>('widgetsList');

const initialState: WidgetsListState = {
	...widgetsListSlice.getInitialState(),
};

const combinedSlice = createSlice({
	name: 'widgetsList',
	initialState,
	reducers: {
		...widgetsListSlice.caseReducers,
		setWidgetsListNormalized(state, action: PayloadAction<NormalizedWidgetListItem[]>) {
			state.data = action.payload;
			state.isFetching = false;
			state.hasFetchError = false;
		},
		removeWidgetByKeySuccess(state, action: PayloadAction<string>) {
			state.data = state.data.filter((item) => item.widgetKey !== action.payload);
			state.isFetching = false;
		},
	},
});

export const {
	fetchDataStart,
	fetchDataError,
	setDataLoaded,
	deleteDataSuccess,
	resetError,
	setWidgetsListNormalized,
	removeWidgetByKeySuccess,
} = combinedSlice.actions;

export const fetchWidgetsListData =
	() => async (dispatch: Dispatch<AnyAction>) => {
		dispatch(combinedSlice.actions.fetchDataStart());
		try {
			const response = await apiUrl.get(API_ENDPOINTS.getWidgetsList);
			const normalized = parseWidgetsListResponse(response.data);
			dispatch(combinedSlice.actions.setWidgetsListNormalized(normalized));
		} catch (error) {
			handleResponseError(error, (errorMessage, isNetworkError) => {
				dispatch(
					combinedSlice.actions.fetchDataError({ errorMessage, isNetworkError }),
				);
			});
		}
	};

export const deleteWidgetData =
	(widgetKey: string) => async (dispatch: Dispatch<AnyAction>) => {
		try {
			await apiUrl.delete(`${API_ENDPOINTS.deleteWidget}${widgetKey}`);
			dispatch(combinedSlice.actions.removeWidgetByKeySuccess(widgetKey));
			return true;
		} catch (error) {
			handleResponseError(error, (errorMessage, isNetworkError) => {
				dispatch(
					combinedSlice.actions.fetchDataError({ errorMessage, isNetworkError }),
				);
			});
			return false;
		}
	};

export default combinedSlice.reducer;
export { combinedSlice as widgetsListSlice };
